var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import '../icon/Icon.js';
/**
 * Pill-style segmented control with an animated indicator that slides
 * to the active option. Equal-width segments. The active slider's
 * background follows the `variant` (and optionally `special` gradient)
 * just like `<simple-button>`.
 *
 * Emits `tab-change` with `{ value }` when the user picks a new segment.
 */
let SlidingTabs = class SlidingTabs extends LitElement {
    constructor() {
        super(...arguments);
        this.options = [];
        this.value = '';
        this.size = 'small';
        this.variant = 'primary';
        this.special = false;
        this.disabled = false;
    }
    _activeIndex() {
        const idx = this.options.findIndex((o) => o.value === this.value);
        return idx >= 0 ? idx : 0;
    }
    _onPick(value) {
        if (this.disabled || value === this.value)
            return;
        this.value = value;
        this.dispatchEvent(new CustomEvent('tab-change', {
            detail: { value },
            bubbles: true,
            composed: true
        }));
    }
    render() {
        const count = Math.max(this.options.length, 1);
        const segWidthPct = 100 / count;
        const activeIdx = this._activeIndex();
        const trackStyle = `--seg-width: ${segWidthPct}%; --seg-index: ${activeIdx};`;
        return html `
      <div class="track" style=${trackStyle} role="tablist">
        <span class="slider" aria-hidden="true"></span>
        ${this.options.map((opt) => html `
            <button
              type="button"
              class="segment ${opt.value === this.value ? 'active' : ''}"
              role="tab"
              aria-selected=${opt.value === this.value}
              ?disabled=${this.disabled}
              @click=${() => this._onPick(opt.value)}
            >
              ${opt.iconName
            ? html `<span class="icon"><basic-icon .name=${opt.iconName}></basic-icon></span>`
            : opt.icon
                ? html `<span class="icon">${unsafeHTML(opt.icon)}</span>`
                : ''}
              <span>${opt.label}</span>
            </button>
          `)}
      </div>
    `;
    }
};
SlidingTabs.styles = css `
    :host {
      display: inline-block;
      font-family: inherit;
    }

    .track {
      position: relative;
      display: flex;
      gap: 2px;
      padding: 2px;
      background: #f0f3f7;
      border-radius: 8px;
      overflow: hidden;
    }

    .slider {
      position: absolute;
      top: 2px;
      left: 2px;
      width: calc(var(--seg-width, 50%) - 2px);
      height: calc(100% - 4px);
      border-radius: 6px;
      transition: transform 0.2s, background 0.2s;
      z-index: 0;
      transform: translateX(calc(var(--seg-index, 0) * 100%));
    }

    .segment {
      position: relative;
      z-index: 1;
      flex: 1;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      background: transparent;
      border: none;
      font: inherit;
      font-weight: 600;
      color: #4a5868;
      cursor: pointer;
      transition: color 0.2s;
      white-space: nowrap;
    }

    .segment:hover {
      color: #2a3440;
    }

    .segment.active,
    .segment.active:hover {
      color: #fff;
    }

    .segment:focus-visible {
      outline: 2px solid currentColor;
      outline-offset: 2px;
      border-radius: 4px;
    }

    /* ── Sizes ────────────────────────────────────────────── */
    :host([size='small']) .segment {
      font-size: 13px;
      padding: 7px 10px;
    }
    :host([size='medium']) .segment {
      font-size: 14px;
      padding: 9px 14px;
    }
    :host([size='large']) .segment {
      font-size: 16px;
      padding: 11px 18px;
    }

    /* ── Variants on the active slider ────────────────────── */
    :host([variant='primary']) .slider {
      background: #0078d4;
    }
    :host([variant='secondary']) .slider {
      background: #6c757d;
    }
    :host([variant='secondary']) .segment.active {
      color: #fff;
    }
    :host([variant='success']) .slider {
      background: #28a745;
    }
    :host([variant='danger']) .slider {
      background: #dc3545;
    }
    :host([variant='warning']) .slider {
      background: #ffc107;
    }
    :host([variant='warning']) .segment.active {
      color: #1a2433;
    }

    /* ── Special (gradient) per variant ───────────────────── */
    :host([variant='primary'][special]) .slider {
      background: linear-gradient(135deg, #0078d4, #00aaff);
    }
    :host([variant='secondary'][special]) .slider {
      background: linear-gradient(135deg, #6c757d, #adb5bd);
    }
    :host([variant='success'][special]) .slider {
      background: linear-gradient(135deg, #28a745, #5cd65c);
    }
    :host([variant='danger'][special]) .slider {
      background: linear-gradient(135deg, #dc3545, #ff6f71);
    }
    :host([variant='warning'][special]) .slider {
      background: linear-gradient(135deg, #ffc107, #ffe066);
    }

    /* ── Disabled state ───────────────────────────────────── */
    :host([disabled]) .track {
      opacity: 0.6;
    }
    :host([disabled]) .segment {
      cursor: not-allowed;
    }

    /* ── Icon slot ────────────────────────────────────────── */
    .icon {
      display: inline-flex;
      flex-shrink: 0;
    }
    .icon svg {
      width: 14px;
      height: 14px;
    }
    :host([size='large']) .icon svg {
      width: 16px;
      height: 16px;
    }
  `;
__decorate([
    property({ type: Array })
], SlidingTabs.prototype, "options", void 0);
__decorate([
    property({ type: String })
], SlidingTabs.prototype, "value", void 0);
__decorate([
    property({ type: String, reflect: true })
], SlidingTabs.prototype, "size", void 0);
__decorate([
    property({ type: String, reflect: true })
], SlidingTabs.prototype, "variant", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SlidingTabs.prototype, "special", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SlidingTabs.prototype, "disabled", void 0);
SlidingTabs = __decorate([
    customElement('sliding-tabs')
], SlidingTabs);
export { SlidingTabs };
//# sourceMappingURL=SlidingTabs.js.map